import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Subject, switchMap, timer } from 'rxjs';

import { getSignedUrlTimeRemainingMs } from '@core/time-legacy';

const DEFAULT_MIN_INTERVAL_MS = 30 * 1000;

export type SignedUrlRefreshScheduler = {
  schedule: (urls: Array<string | null | undefined>) => void;
};

export function createSignedUrlRefreshScheduler(options: {
  refresh: () => void;
  minIntervalMs?: number;
}): SignedUrlRefreshScheduler {
  const destroyRef = inject(DestroyRef);
  const minIntervalMs = options.minIntervalMs ?? DEFAULT_MIN_INTERVAL_MS;
  const trigger$ = new Subject<number>();

  trigger$
    .pipe(
      switchMap((delayMs, index) => timer(index === 0 && delayMs === minIntervalMs ? 0 : delayMs)),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe(options.refresh);

  return {
    schedule: urls => {
      const remainingTimes = urls
        .filter((url): url is string => !!url)
        .map(getSignedUrlTimeRemainingMs)
        .filter((ms): ms is number => ms !== null);

      if (!remainingTimes.length) return;

      const earliestExpiryMs = Math.min(...remainingTimes);
      const delay = Math.max(earliestExpiryMs, minIntervalMs);
      trigger$.next(delay);
    },
  };
}
